// ============================================================================
//  authUrl — snapshot of the auth parameters the email link arrived with.
//
//  This has to run BEFORE createClient(). supabase-js is configured with
//  detectSessionInUrl: true, and the first thing it does is consume the URL
//  fragment and then scrub it. Any component that reads window.location later
//  is reading a URL the client has already cleaned, which is why the old
//  AuthCallback could not tell an expired link from a fresh one and simply
//  guessed.
//
//  Ordering is guaranteed, not hoped for: lib/supabase.js imports this module,
//  so ES module evaluation runs everything below before createClient is
//  called. Do not remove that import.
// ============================================================================

function read() {
  if (typeof window === "undefined") return { params: null };

  // Supabase has used both halves of the URL over the years, and still does:
  // the token_hash templates land in the query string, the older implicit
  // links land in the fragment, and errors can come back in either. Merge
  // them, query first so an explicit ?token_hash wins.
  const merged = new URLSearchParams();
  for (const src of [
    new URLSearchParams(window.location.hash.replace(/^#/, "")),
    new URLSearchParams(window.location.search),
  ]) {
    for (const [k, v] of src) merged.set(k, v);
  }
  return { params: merged };
}

const { params } = read();
const get = (k) => params?.get(k) || null;

export const authUrl = {
  // New, scanner-proof path: the email links to our own page and the token is
  // only spent by a POST from the browser.
  tokenHash: get("token_hash"),
  type: get("type"),

  // Legacy implicit path, still arriving from any email sent before the
  // templates were changed.
  accessToken: get("access_token"),
  refreshToken: get("refresh_token"),

  // Failure, from either half of the URL.
  error: get("error") || get("error_code"),
  errorCode: get("error_code"),
  errorDescription: get("error_description"),
};

/** True when the link carried nothing we know how to act on. */
export const authUrlIsEmpty =
  !authUrl.tokenHash && !authUrl.accessToken && !authUrl.error;

/**
 * Strip the auth parameters out of the address bar. supabase-js does this for
 * the fragment it consumes; a ?token_hash= would otherwise sit in history and
 * in any screenshot the man sends when he asks for help.
 */
export function scrubAuthUrl() {
  if (typeof window === "undefined" || !window.history?.replaceState) return;
  window.history.replaceState({}, document.title, window.location.pathname);
}
